import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { findPost, POSTS } from "@/data/posts";
import CTABand from "@/components/CTABand";
import { useSeo } from "@/lib/seo";
import { useReveal } from "@/lib/reveal";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const p = findPost(slug);

  useSeo({
    title: p ? p.title : "Article",
    description: p ? p.excerpt : "RentAnything.ai blog post",
    path: `/resources/${slug || ""}`,
  });
  useReveal();

  if (!p) return <Navigate to="/resources" replace />;

  const others = POSTS.filter((x) => x.slug !== p.slug).slice(0, 2);

  return (
    <>
      <article className="ra-mesh">
        <div className="ra-container py-20 sm:py-24">
          <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ra-ink/70 hover:text-ra-ocean" data-testid="post-back-link">
            <ArrowLeft size={14} /> All field notes
          </Link>

          <header className="mt-8 max-w-3xl">
            <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-ra-ink/55">
              <span>{fmt(p.date)}</span>
              <span className="h-1 w-1 rounded-full bg-ra-ink/30" />
              <span className="inline-flex items-center gap-1"><Clock size={11} /> {p.readMins} min read</span>
            </div>
            <h1 className="mt-4 ra-h1" data-testid="post-title">{p.title}</h1>
            <p className="mt-5 ra-lead">{p.excerpt}</p>
          </header>

          <div className={`mt-10 h-64 sm:h-80 w-full rounded-3xl border border-ra-line bg-gradient-to-br ${p.accent} relative overflow-hidden`}>
            <div className="absolute inset-0 ra-grain opacity-50" />
          </div>

          <div className="mx-auto mt-12 max-w-3xl space-y-6 text-[17px] leading-[1.7] text-ra-ink/85">
            {p.body.map((para, i) => (
              <p key={i} className="reveal">{para}</p>
            ))}
          </div>
        </div>
      </article>

      {/* RELATED */}
      <section className="ra-section bg-ra-paper border-y border-ra-line">
        <div className="ra-container">
          <div className="max-w-2xl reveal">
            <span className="ra-eyebrow">Keep reading</span>
            <h2 className="mt-3 ra-h2">More field notes</h2>
          </div>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {others.map((o) => (
              <Link key={o.slug} to={`/resources/${o.slug}`} className="ra-card reveal group" data-testid={`post-related-${o.slug}`}>
                <span className="ra-eyebrow !text-[10px]">{fmt(o.date)} · {o.readMins} min</span>
                <h3 className="mt-3 font-display text-lg font-semibold leading-snug text-ra-ink group-hover:text-ra-ocean transition-colors">{o.title}</h3>
                <p className="mt-2 text-sm text-ra-ink/70">{o.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
